import React, { useEffect, useState } from "react";
import { EntityLink } from "@/components/EntityLink";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { AlertTriangle, CalendarClock, Mail, Store, Users, RefreshCw, Loader2 } from "lucide-react";

const keyOf = (it) => `${it.entity_type}:${it.id}`;

const expiryBadge = (days) => {
  if (days < 0) return { label: "Scaduto", cls: "bg-red-600 hover:bg-red-600" };
  if (days <= 15) return { label: `${days}g`, cls: "bg-orange-500 hover:bg-orange-500 text-black" };
  return { label: `${days}g`, cls: "bg-amber-500 hover:bg-amber-500 text-black" };
};

export default function AdminExpiringDocs() {
  const [days, setDays] = useState("60");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [sending, setSending] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/documents/expiring", { params: { days: Number(days) } })
      .then((r) => { setItems(r.data.items || []); setSelected([]); })
      .catch(() => toast.error("Errore caricamento"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  const allKeys = items.map(keyOf);
  const allSelected = allKeys.length > 0 && selected.length === allKeys.length;
  const toggleAll = () => setSelected(allSelected ? [] : allKeys);
  const toggle = (it) => setSelected((s) => s.includes(keyOf(it)) ? s.filter((x) => x !== keyOf(it)) : [...s, keyOf(it)]);

  const sendReminders = async () => {
    const chosen = items.filter((it) => selected.includes(keyOf(it)));
    if (!chosen.length) { toast.error("Seleziona almeno un elemento"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/admin/documents/send-reminders", { items: chosen.map((it) => ({ entity_type: it.entity_type, id: it.id })) });
      toast.success(`Reminder inviati: ${data.sent}`);
      setSelected([]);
    } catch (err) {
      toast.error("Errore invio reminder");
    } finally { setSending(false); }
  };

  return (
    <div className="space-y-6" data-testid="expiring-docs-page">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-lg bg-amber-500/15 text-amber-500 flex items-center justify-center"><CalendarClock className="w-5 h-5" /></div>
          <div>
            <h1 className="font-head text-2xl font-semibold">Documenti in scadenza</h1>
            <p className="text-sm text-muted-foreground">Dealer e clienti con documento amministratore in scadenza o scaduto.</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 ml-auto">
          <span className="text-sm text-muted-foreground">Soglia</span>
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="h-9 w-36" data-testid="expiry-threshold"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="30">Entro 30 giorni</SelectItem>
              <SelectItem value="60">Entro 60 giorni</SelectItem>
              <SelectItem value="90">Entro 90 giorni</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" className="gap-2" onClick={load} data-testid="expiry-refresh"><RefreshCw className="w-4 h-4" /> Aggiorna</Button>
        </div>
      </div>

      <Card className="p-4 glow-border">
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="expiry-select-all" />
            Seleziona tutti ({items.length})
          </label>
          <Button className="gap-2 ml-auto" disabled={sending || !selected.length} onClick={sendReminders} data-testid="expiry-send-reminders">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Invia reminder ({selected.length})
          </Button>
        </div>

        {loading ? (
          <p className="text-muted-foreground text-sm py-8 text-center">Caricamento…</p>
        ) : items.length === 0 ? (
          <p className="text-muted-foreground text-sm py-8 text-center" data-testid="expiry-empty">Nessun documento in scadenza entro {days} giorni. 🎉</p>
        ) : (
          <div className="space-y-2">
            {items.map((it) => {
              const b = expiryBadge(it.days_left);
              return (
                <div key={keyOf(it)} className="flex items-center gap-3 p-3 rounded-lg bg-secondary/40" data-testid={`expiry-row-${it.entity_type}-${it.id}`}>
                  <Checkbox checked={selected.includes(keyOf(it))} onCheckedChange={() => toggle(it)} data-testid={`expiry-check-${it.id}`} />
                  <div className="w-9 h-9 rounded-lg bg-primary/15 text-primary flex items-center justify-center shrink-0">
                    {it.entity_type === "dealer" ? <Store className="w-4 h-4" /> : <Users className="w-4 h-4" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate"><EntityLink kind={it.entity_type === "dealer" ? "account" : "customer"} id={it.id} data-testid={`expiry-profile-link-${it.entity_type}-${it.id}`}>{it.company_name || it.name || "—"}</EntityLink> <span className="text-xs text-muted-foreground">({it.entity_type === "dealer" ? "Dealer" : "Cliente"})</span></p>
                    <p className="text-xs text-muted-foreground truncate">Amm.: {it.admin_name || "—"} · {it.email || "nessuna email"}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <Badge className={b.cls}>{it.days_left < 0 ? "Scaduto" : `Scade tra ${it.days_left}g`}</Badge>
                    <p className="text-xs text-muted-foreground mt-1">{it.data_scadenza}</p>
                    <p className="text-xs mt-0.5" data-testid={`expiry-last-${it.id}`}>
                      {it.last_reminder_at
                        ? <span className="text-teal-600">Sollecito: {new Date(it.last_reminder_at).toLocaleDateString("it-IT")}</span>
                        : <span className="text-muted-foreground/70">Mai sollecitato</span>}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
